import { devtools } from 'zustand/middleware';
import { create } from 'zustand';
import { useMemo } from 'react';
import { type LatLngExpression } from 'leaflet';
import { boundingBoxToLatLngBounds, getFlyDurationFromDistance, getLatLngBoundsCenter } from '@pkgs/components/utils/map';
import { isProductionClient } from '@pkgs/components/consts/environment';
import { type BoundingBoxDto } from '@ev-tech/search-modules-api';

type MapStoreState = {
  popupPosition: LatLngExpression | undefined;
  activeMarkerId: string | undefined;
  activeClusterIds: Set<string>;
  activeClusterIndex: number;
};

type MapStoreActions = {
  setActiveMarkerId: (id: string, position: LatLngExpression) => void;
  setActiveClusterIds: (ids: Set<string>, position: LatLngExpression) => void;
  setActiveClusterIndex: (index: number) => void;
  reset: () => void;
  resetActiveMarkerId: () => void;
  updateMapBounds: (bounds: BoundingBoxDto) => void;
};

export const useMapStore = create<MapStoreState & MapStoreActions>()(
  devtools(
    (set, get) => ({
      activeMarkerId: undefined,
      activeClusterIds: new Set(),
      popupPosition: undefined,
      activeClusterIndex: 0,
      setActiveMarkerId: (id, position) => set({ activeMarkerId: id, popupPosition: position }, undefined, 'setActiveMarkerId'),
      setActiveClusterIds: (ids, position) => set({ activeClusterIds: ids, popupPosition: position }, undefined, 'setActiveClusterIds'),
      setActiveClusterIndex: (index) => set({ activeClusterIndex: index }, undefined, 'setActiveClusterIndex'),
      reset: () =>
        set(
          { activeMarkerId: undefined, activeClusterIds: new Set(), popupPosition: undefined, activeClusterIndex: 0 },
          undefined,
          'reset',
        ),
      resetActiveMarkerId: () => set({ activeMarkerId: undefined }, undefined, 'resetActiveMarkerId'),
      updateMapBounds: (bounds) => {
        const { reset, popupPosition } = get();
        const map = window._leaflet_map;
        if (!map) return;

        if (popupPosition) {
          map.closePopup();
          reset();
        }

        const latLngBounds = boundingBoxToLatLngBounds(bounds);

        const currentBounds = map.getBounds();
        // If the current view is the same as the new view, don't change the view.
        if (currentBounds.equals(latLngBounds)) return;

        // Set map view to place bounding box after user searched for place.
        // Don't set view if user clicked "search this area" on map.
        const distanceInKm = Math.round(map.distance(map.getCenter(), getLatLngBoundsCenter(latLngBounds)) / 1000);

        // If distance from current view to new view is more than 1000 km, jump to new view instantly,
        // otherwise fly to new view with animation.
        // 'noMoveStart' is set to flag this as a programmatic move and not a user interaction.
        if (distanceInKm > 1000) {
          map.fitBounds(latLngBounds, { noMoveStart: true });
        } else {
          map.flyToBounds(latLngBounds, { duration: getFlyDurationFromDistance(distanceInKm), noMoveStart: true });
        }
      },
    }),
    {
      name: 'search-modules',
      store: 'map-store',
      enabled: !isProductionClient,
    },
  ),
);

export const useActivePropertyIds = () => {
  const activeMarkerId = useMapStore((state) => state.activeMarkerId);
  const activeClusterIds = useMapStore((state) => state.activeClusterIds);
  return useMemo(() => (activeMarkerId ? new Set([activeMarkerId]) : activeClusterIds), [activeMarkerId, activeClusterIds]);
};
